import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, Receipt, Wallet, Image as ImageIcon, X } from "lucide-react";

/**
 * Current Shift Invoice Summary — unified ledger of invoices + cashflow for
 * the active shift, with per-row Eye-icon preview, grouped sections, and a
 * bold total card. Live-syncs every 4 s and on `refreshKey` bumps.
 */
export default function CurrentShiftSummary({ shift, refreshKey = 0 }) {
  const [invoices, setInvoices] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);

  const load = async () => {
    if (!shift?.id) return;
    try {
      const [invR, cfR] = await Promise.all([
        api.get("/invoices", { params: { shift_id: shift.id } }),
        api.get("/cashflow", { params: { shift_id: shift.id } }),
      ]);
      setInvoices(invR.data || []);
      setCashflow(cfR.data || []);
    } catch {
      /* keep last good copy */
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift?.id, refreshKey]);

  // Deduction count + total — only DRAWER PAYOUTs and cash invoices that
  // actually touch the till (matches `expected_cash` math on the backend).
  const totals = useMemo(() => {
    const drawerInvoices = invoices.filter(
      (i) => !i.historical && i.shift_id && (i.payment_method === "CASH" || i.status === "PAID_CASH"),
    );
    const drawerCash = cashflow.filter(
      (c) => !c.historical && (c.payment_source || "DRAWER") === "DRAWER" && c.kind === "PAYOUT",
    );
    const invSum = drawerInvoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
    const cfSum = drawerCash.reduce((s, c) => s + Number(c.amount || 0), 0);
    const count = invoices.length + cashflow.length;
    return {
      count,
      deductions: invSum + cfSum,
      invCount: invoices.length,
      cfCount: cashflow.length,
    };
  }, [invoices, cashflow]);

  return (
    <Card className="p-5" data-testid="current-shift-summary">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-lg leading-tight">Current Shift Invoice Summary</h3>
          <p className="text-[11px] text-slate-500 tracking-wide">
            Σύνοψη Βάρδιας · Live sync every 4s · Tap 👁 to preview the original photo
          </p>
        </div>
        <Badge className="bg-slate-900 text-white">
          {totals.count} items · €{totals.deductions.toFixed(2)} deductions
        </Badge>
      </div>

      {/* Supplier Invoices */}
      <Section
        icon={<Receipt className="h-4 w-4" />}
        title="Supplier Invoices · Τιμολόγια"
        count={totals.invCount}
        empty="No invoices in this shift yet."
        data-testid="summary-section-invoices"
      >
        {invoices.map((inv) => (
          <Row
            key={inv.id}
            testId={`summary-inv-row-${inv.id}`}
            primary={inv.supplier_name || "Unknown supplier"}
            secondary={[
              inv.invoice_number && `#${inv.invoice_number}`,
              inv.issue_date,
              inv.status,
              inv.payment_method,
              inv.historical && "🕓 Historical",
              inv.shift_id == null && !inv.historical && "Bypassed",
            ].filter(Boolean).join(" · ")}
            amount={inv.total_amount}
            onPreview={() => setPreviewItem({ kind: "invoice", item: inv })}
          />
        ))}
      </Section>

      {/* Cash Expenses / Salaries / Advances */}
      <Section
        icon={<Wallet className="h-4 w-4" />}
        title="Cash Expenses · Salaries · Advances"
        count={totals.cfCount}
        empty="No cashflow entries in this shift yet."
        data-testid="summary-section-cashflow"
      >
        {cashflow.map((c) => {
          const label = c.category
            ? `${c.employee_name || "Staff"} · ${c.category}`
            : c.kind === "CASH_DROP"
            ? "Cash drop"
            : (c.note || "Payout");
          const ps = c.payment_source || "DRAWER";
          const tags = [
            ps !== "DRAWER" && ps,
            c.historical && "🕓 Historical",
            c.transaction_date,
          ].filter(Boolean).join(" · ");
          return (
            <Row
              key={c.id}
              testId={`summary-cf-row-${c.id}`}
              primary={label}
              secondary={tags || c.note}
              amount={c.amount}
              negative={c.kind === "PAYOUT"}
              onPreview={() => setPreviewItem({ kind: "cashflow", item: c })}
            />
          );
        })}
      </Section>

      {/* Bold totals card */}
      <div
        className="mt-5 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 grid grid-cols-2 gap-4"
        data-testid="summary-totals-card"
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
            Total Shift Transactions Count
          </div>
          <div className="font-mono text-4xl font-bold mt-1" data-testid="summary-total-count">
            {totals.count}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
            Total Shift Deductions Amount
          </div>
          <div className="font-mono text-4xl font-bold mt-1 text-amber-300" data-testid="summary-total-deductions">
            €{totals.deductions.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-4xl" data-testid="summary-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                {previewItem?.kind === "invoice" ? "Invoice Preview" : "Cashflow Receipt Preview"}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setPreviewItem(null)}>
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {previewItem && <PreviewBody data={previewItem} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Section({ icon, title, count, empty, children, ...rest }) {
  return (
    <div className="mb-4" {...rest}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">
        {icon}
        <span>{title}</span>
        <span className="ml-auto text-slate-400">{count}</span>
      </div>
      {count === 0 ? (
        <p className="text-xs text-slate-400 italic py-3 text-center">{empty}</p>
      ) : (
        <div className="space-y-1.5">{children}</div>
      )}
    </div>
  );
}

function Row({ primary, secondary, amount, negative, onPreview, testId }) {
  const amt = Number(amount || 0);
  return (
    <div
      className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5 hover:bg-slate-50 transition-colors"
      data-testid={testId}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900 truncate">{primary}</div>
        {secondary && (
          <div className="text-[11px] text-slate-500 truncate">{secondary}</div>
        )}
      </div>
      <div
        className={`font-mono font-bold tabular-nums ${
          negative ? "text-red-700" : "text-slate-900"
        }`}
      >
        {negative ? "−" : ""}€{amt.toFixed(2)}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onPreview}
        className="h-8 w-8 p-0 shrink-0"
        title="Preview"
        data-testid={`${testId}-eye`}
      >
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  );
}

function PreviewBody({ data }) {
  const { kind, item } = data;
  // Resolve the image source — invoices store either invoice_image_url or
  // invoice_image_path; cashflow uses receipt_image_url / receipt_image_path.
  const imgSrc =
    kind === "invoice"
      ? fileUrl(item.invoice_image_url || item.invoice_image_path)
      : fileUrl(item.receipt_image_url || item.receipt_image_path);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-slate-50 rounded-lg border border-slate-200 min-h-[320px] flex items-center justify-center overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt="receipt"
            className="max-h-[60vh] max-w-full object-contain"
            data-testid="summary-preview-image"
          />
        ) : (
          <div className="text-center text-slate-400 p-6">
            <ImageIcon className="h-10 w-10 mx-auto mb-2" />
            <div className="text-xs">No photo on file</div>
          </div>
        )}
      </div>
      <div className="space-y-2 text-sm">
        {kind === "invoice" ? (
          <>
            <Detail label="Supplier" value={item.supplier_name} />
            <Detail label="Invoice #" value={item.invoice_number} />
            <Detail label="Issue Date" value={item.issue_date} />
            <Detail label="Status" value={item.status} />
            <Detail label="Payment Method" value={item.payment_method} />
            {item.cheque_number && <Detail label="Cheque #" value={item.cheque_number} />}
            {item.cheque_maturity && <Detail label="Maturity" value={item.cheque_maturity} />}
            <hr className="my-2" />
            <Detail label="Net" value={`€${Number(item.net_amount || 0).toFixed(2)}`} />
            <Detail label={`VAT @ ${item.vat_rate || 0}%`} value={`€${Number(item.vat_amount || 0).toFixed(2)}`} />
            <Detail label="Total" value={`€${Number(item.total_amount || 0).toFixed(2)}`} bold />
            {item.historical && <Detail label="Historical" value="🕓 Backdated entry" />}
            {item.shift_id == null && !item.historical && (
              <Detail label="Routing" value="Bypassed active shift" />
            )}
          </>
        ) : (
          <>
            <Detail label="Type" value={item.kind} />
            <Detail label="Payment Source" value={item.payment_source || "DRAWER"} />
            {item.category && <Detail label="Category" value={item.category} />}
            {item.employee_name && <Detail label="Employee" value={item.employee_name} />}
            <Detail label="Amount" value={`€${Number(item.amount || 0).toFixed(2)}`} bold />
            {item.transaction_date && (
              <Detail label="Backdated To" value={item.transaction_date} />
            )}
            {item.cheque_number && <Detail label="Cheque #" value={item.cheque_number} />}
            {item.cheque_supplier && <Detail label="Payee" value={item.cheque_supplier} />}
            {item.note && <Detail label="Note" value={item.note} />}
            <Detail label="Created" value={new Date(item.created_at).toLocaleString()} />
          </>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value, bold }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
      <span className={`text-slate-900 ${bold ? "font-bold text-base" : "font-semibold"}`}>
        {value || "—"}
      </span>
    </div>
  );
}
